import React, { useState, useEffect, useCallback } from 'react';
import {
  Store, Plus, Search, DollarSign, Eye, MessageSquare, Loader2,
  Tag, ArrowRight, Handshake, ChevronRight, Trash2, Check, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function WholesaleMarketplace() {
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('browse'); // browse, my-listings
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showOffer, setShowOffer] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    make: '', model: '', year: '', wholesale_price: '', mileage: '', condition: 'good', description: '', listing_type: 'wholesale'
  });
  const [offerForm, setOfferForm] = useState({ amount: '', message: '' });

  const fetchData = useCallback(async () => {
    try {
      const [browseRes, mineRes] = await Promise.all([
        api.get('/api/wholesale/listings'),
        api.get('/api/wholesale/listings?mine=true')
      ]);
      setListings(browseRes.data);
      setMyListings(mineRes.data);
    } catch (err) {
      console.error('Failed to fetch wholesale listings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model || !form.year || !form.wholesale_price) {
      toast.error('Make, model, year, and price are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/wholesale/listings', {
        ...form,
        year: parseInt(form.year),
        wholesale_price: parseFloat(form.wholesale_price),
        mileage: form.mileage ? parseInt(form.mileage) : null,
      });
      toast.success('Listing created!');
      setShowCreate(false);
      setForm({ make: '', model: '', year: '', wholesale_price: '', mileage: '', condition: 'good', description: '', listing_type: 'wholesale' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSaving(false);
    }
  };

  const handleOffer = async () => {
    if (!offerForm.amount) { toast.error('Enter an offer amount'); return; }
    setSaving(true);
    try {
      await api.post(`/api/wholesale/listings/${showOffer}/offer`, {
        amount: parseFloat(offerForm.amount),
        message: offerForm.message || null
      });
      toast.success('Offer submitted!');
      setShowOffer(null);
      setOfferForm({ amount: '', message: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit offer');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptOffer = async (listingId, offerId) => {
    try {
      await api.post(`/api/wholesale/listings/${listingId}/offers/${offerId}/accept`);
      toast.success('Offer accepted!');
      setSelectedListing(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to accept offer');
    }
  };

  const handleDelete = async (listingId) => {
    try {
      await api.delete(`/api/wholesale/listings/${listingId}`);
      toast.success('Listing deleted');
      setSelectedListing(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete listing');
    }
  };

  const filteredListings = (tab === 'browse' ? listings : myListings).filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.make?.toLowerCase().includes(q) || l.model?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  // Detail View
  if (selectedListing) {
    const isOwner = myListings.some(l => l.id === selectedListing.id);
    return (
      <div data-testid="wholesale-listing-detail">
        <Button variant="outline" className="rounded-xl mb-4" onClick={() => setSelectedListing(null)} data-testid="wholesale-back-btn">
          <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Back
        </Button>
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedListing.year} {selectedListing.make} {selectedListing.model}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-2xl font-bold text-green-600">${selectedListing.wholesale_price?.toLocaleString()}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedListing.listing_type === 'trade' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {selectedListing.listing_type === 'trade' ? 'Trade' : 'Wholesale'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {!isOwner && (
                <Button className="rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white" onClick={() => { setShowOffer(selectedListing.id); setOfferForm({ amount: '', message: '' }); }} data-testid="wholesale-make-offer-btn">
                  <Handshake className="w-4 h-4 mr-1" /> Make Offer
                </Button>
              )}
              {isOwner && (
                <Button variant="outline" className="rounded-xl text-red-500" onClick={() => handleDelete(selectedListing.id)} data-testid="wholesale-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Condition', value: selectedListing.condition },
              { label: 'Mileage', value: selectedListing.mileage ? `${selectedListing.mileage.toLocaleString()} mi` : 'N/A' },
              { label: 'VIN', value: selectedListing.vin || 'N/A' },
              { label: 'Views', value: selectedListing.views || 0 },
            ].map(f => (
              <div key={f.label} className="bg-gray-50 rounded-xl p-3">
                <span className="text-xs text-gray-500 uppercase">{f.label}</span>
                <p className="font-semibold text-gray-900 text-sm mt-0.5">{f.value}</p>
              </div>
            ))}
          </div>

          {selectedListing.description && (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-700">{selectedListing.description}</p>
            </div>
          )}

          <p className="text-xs text-gray-400">Listed by {selectedListing.company_name || selectedListing.dealer_name} &middot; {timeAgo(selectedListing.created_at)}</p>

          {/* Offers (visible to owner) */}
          {isOwner && selectedListing.offers && selectedListing.offers.length > 0 && !selectedListing.offers[0]?.count && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Offers ({selectedListing.offers.length})</h3>
              <div className="space-y-3">
                {selectedListing.offers.map(offer => (
                  <div key={offer.id} className={`p-4 rounded-xl border ${offer.status === 'accepted' ? 'bg-green-50 border-green-200' : offer.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{offer.buyer_company || offer.buyer_name}</p>
                        <p className="text-lg font-bold text-green-600">${offer.amount?.toLocaleString()}</p>
                        {offer.message && <p className="text-sm text-gray-500 mt-1">{offer.message}</p>}
                      </div>
                      {offer.status === 'pending' && (
                        <Button size="sm" className="rounded-xl bg-green-500 text-white" onClick={() => handleAcceptOffer(selectedListing.id, offer.id)} data-testid={`accept-offer-${offer.id}`}>
                          <Check className="w-4 h-4 mr-1" /> Accept
                        </Button>
                      )}
                      {offer.status === 'accepted' && <span className="text-green-600 font-medium text-sm">Accepted</span>}
                      {offer.status === 'rejected' && <span className="text-red-500 font-medium text-sm">Rejected</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div data-testid="wholesale-marketplace">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Available Listings</p>
          <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="wholesale-available-count">{listings.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">My Listings</p>
          <p className="text-3xl font-bold text-indigo-600 mt-1" data-testid="wholesale-my-count">{myListings.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">My Offers Received</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{myListings.reduce((sum, l) => sum + (l.offers?.length || 0), 0)}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search vehicles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-10 rounded-xl" data-testid="wholesale-search" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button onClick={() => setTab('browse')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${tab === 'browse' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`} data-testid="wholesale-tab-browse">Browse</button>
            <button onClick={() => setTab('my-listings')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${tab === 'my-listings' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`} data-testid="wholesale-tab-mine">My Listings</button>
          </div>
          <Button className="rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg" onClick={() => setShowCreate(true)} data-testid="wholesale-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New Listing
          </Button>
        </div>
      </div>

      {/* Listings Grid */}
      {filteredListings.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <Store className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{tab === 'browse' ? 'No wholesale listings available from other dealers' : 'You have no active listings'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wholesale-listings-grid">
          {filteredListings.map(listing => (
            <div
              key={listing.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden"
              onClick={() => setSelectedListing(listing)}
              data-testid={`wholesale-card-${listing.id}`}
            >
              {listing.images && listing.images.length > 0 ? (
                <div className="h-40 bg-gray-100 overflow-hidden">
                  <img src={listing.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </div>
              ) : (
                <div className="h-28 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                  <Store className="w-8 h-8 text-gray-300" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{listing.year} {listing.make} {listing.model}</h3>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-400" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold text-green-600">${listing.wholesale_price?.toLocaleString()}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${listing.listing_type === 'trade' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                    {listing.listing_type === 'trade' ? 'Trade' : 'Wholesale'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{listing.condition} &middot; {listing.mileage ? `${listing.mileage.toLocaleString()} mi` : 'N/A'}</span>
                  <span>{listing.company_name || listing.dealer_name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Listing Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Create Wholesale Listing</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-gray-700 font-medium">Make *</Label><Input placeholder="Ford" value={form.make} onChange={e => setForm(p => ({ ...p, make: e.target.value }))} data-testid="wholesale-form-make" /></div>
              <div><Label className="text-gray-700 font-medium">Model *</Label><Input placeholder="F-150" value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} data-testid="wholesale-form-model" /></div>
              <div><Label className="text-gray-700 font-medium">Year *</Label><Input type="number" placeholder="2020" value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} data-testid="wholesale-form-year" /></div>
              <div><Label className="text-gray-700 font-medium">Price *</Label><Input type="number" placeholder="22000" value={form.wholesale_price} onChange={e => setForm(p => ({ ...p, wholesale_price: e.target.value }))} data-testid="wholesale-form-price" /></div>
              <div><Label className="text-gray-700 font-medium">Mileage</Label><Input type="number" placeholder="45000" value={form.mileage} onChange={e => setForm(p => ({ ...p, mileage: e.target.value }))} data-testid="wholesale-form-mileage" /></div>
              <div>
                <Label className="text-gray-700 font-medium">Type</Label>
                <Select value={form.listing_type} onValueChange={v => setForm(p => ({ ...p, listing_type: v }))}>
                  <SelectTrigger data-testid="wholesale-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                    <SelectItem value="trade">Trade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Description</Label>
              <Textarea placeholder="Vehicle details, history, condition notes..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="wholesale-form-desc" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-xl h-11 bg-gradient-to-r from-green-500 to-emerald-500 text-white" data-testid="wholesale-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create Listing
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Make Offer Modal */}
      <Dialog open={!!showOffer} onOpenChange={() => setShowOffer(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Make an Offer</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-gray-700 font-medium">Offer Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input type="number" placeholder="20000" value={offerForm.amount} onChange={e => setOfferForm(p => ({ ...p, amount: e.target.value }))} className="pl-7" data-testid="wholesale-offer-amount" />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Message (optional)</Label>
              <Textarea placeholder="I'm interested in this vehicle..." value={offerForm.message} onChange={e => setOfferForm(p => ({ ...p, message: e.target.value }))} data-testid="wholesale-offer-message" />
            </div>
            <Button onClick={handleOffer} disabled={saving} className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white" data-testid="wholesale-offer-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Handshake className="w-4 h-4 mr-2" />} Submit Offer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
